import React, { useMemo, useState } from 'react';
import { useSiteSettings } from './SiteSettingsContext';
import { formatScheduleDatePill } from './exhibitionsDisplay';
import {
  exhibitionMatchesFilter,
  groupExhibitionsByMonth,
  uniqueCitiesFromExhibitions,
} from './exhibitionsDisplay';

const PREVIEW_COUNT = 6;
const LARGE_LIST_THRESHOLD = 12;
const RECENT_INITIAL = 6;

function ScheduleRow({ row, muted = false }) {
  const pill = formatScheduleDatePill(row.event_start, row.event_end);
  const title = row.title?.trim();
  const where = [row.venue, [row.city, row.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ');
  const endAttr = row.event_end && row.event_end !== row.event_start ? row.event_end : undefined;

  return (
    <li className={`exhibition-schedule-row${muted ? ' exhibition-schedule-row--muted' : ''}`}>
      <div className="exhibition-schedule-row__date" aria-hidden="true">
        <time dateTime={row.event_start}>
          <span className="exhibition-schedule-row__month">{pill.month}</span>
          <span className="exhibition-schedule-row__day">{pill.day}</span>
          <span className="exhibition-schedule-row__year">{pill.year}</span>
        </time>
        {endAttr ? <time dateTime={endAttr} className="visually-hidden" /> : null}
      </div>
      <div className="exhibition-schedule-row__body">
        {title ? <h3 className="exhibition-schedule-row__title">{title}</h3> : null}
        <p className="exhibition-schedule-row__where">
          <span className="exhibition-schedule-row__pin" aria-hidden="true" />
          {where}
        </p>
        {row.details ? <p className="exhibition-schedule-row__details">{row.details}</p> : null}
        {row.external_url ? (
          <a href={row.external_url} className="exhibition-schedule-row__link" target="_blank" rel="noopener noreferrer">
            Event link
          </a>
        ) : null}
      </div>
    </li>
  );
}

function GroupedScheduleList({ groups, muted = false, scrollable = false }) {
  if (!groups.length) {
    return <p className="exhibition-schedule-empty small text-muted mb-0 px-3 py-2">No shows match your filter.</p>;
  }

  return (
    <div className={`exhibition-schedule-list-wrap${scrollable ? ' exhibition-schedule-list-wrap--scroll' : ''}`}>
      {groups.map((group) => (
        <div key={group.key} className="exhibition-schedule-month">
          <h4 className="exhibition-schedule-month__label">{group.label}</h4>
          <ul className="exhibition-schedule-list exhibition-schedule-list--grid">
            {group.items.map((row) => (
              <ScheduleRow key={row.id} row={row} muted={muted} />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function UpcomingSchedule({ items }) {
  const [fullOpen, setFullOpen] = useState(items.length <= LARGE_LIST_THRESHOLD);
  const [query, setQuery] = useState('');
  const [cityFilter, setCityFilter] = useState('');

  const cities = useMemo(() => uniqueCitiesFromExhibitions(items), [items]);
  const countries = useMemo(() => {
    const s = new Set(items.map((r) => String(r.country || '').trim()).filter(Boolean));
    return s.size;
  }, [items]);

  const filtered = useMemo(
    () => items.filter((row) => exhibitionMatchesFilter(row, { query, city: cityFilter })),
    [items, query, cityFilter]
  );

  const previewItems = useMemo(() => filtered.slice(0, PREVIEW_COUNT), [filtered]);
  const previewGroups = useMemo(() => groupExhibitionsByMonth(previewItems), [previewItems]);
  const fullGroups = useMemo(() => groupExhibitionsByMonth(filtered), [filtered]);

  const showFilters = fullOpen && items.length > 4;
  const useScroll = fullOpen && filtered.length > 8;

  return (
    <div className="exhibitions-panel exhibitions-panel--upcoming">
      <div className="exhibitions-panel__head">
        <div className="exhibitions-panel__head-text">
          <h3 className="exhibitions-panel__heading">Upcoming &amp; current</h3>
          <div className="exhibitions-panel__stats">
            <span className="exhibitions-stat-pill">
              {items.length} {items.length === 1 ? 'show' : 'shows'}
            </span>
            {countries > 0 && (
              <span className="exhibitions-stat-pill exhibitions-stat-pill--muted">
                {countries} {countries === 1 ? 'country' : 'countries'}
              </span>
            )}
          </div>
        </div>
      </div>

      {!fullOpen && (
        <>
          <GroupedScheduleList groups={previewGroups} />
          {filtered.length > PREVIEW_COUNT && (
            <button
              type="button"
              className="exhibition-schedule-more btn btn-link btn-sm"
              onClick={() => setFullOpen(true)}
            >
              View full schedule ({filtered.length} shows)
            </button>
          )}
        </>
      )}

      {fullOpen && (
        <>
          {showFilters && (
            <div className="exhibition-schedule-toolbar">
              <label className="exhibition-schedule-search">
                <span className="visually-hidden">Search exhibitions</span>
                <input
                  type="search"
                  className="form-control form-control-sm"
                  placeholder="Search city, venue, or show name…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
              {cities.length > 1 && (
                <div className="exhibition-schedule-chips" role="group" aria-label="Filter by city">
                  <button
                    type="button"
                    className={`exhibition-schedule-chip${cityFilter === '' ? ' is-active' : ''}`}
                    onClick={() => setCityFilter('')}
                  >
                    All cities
                  </button>
                  {cities.slice(0, 12).map((city) => (
                    <button
                      key={city}
                      type="button"
                      className={`exhibition-schedule-chip${cityFilter === city ? ' is-active' : ''}`}
                      onClick={() => setCityFilter(city)}
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <GroupedScheduleList groups={fullGroups} scrollable={useScroll} />
          {items.length > LARGE_LIST_THRESHOLD && (
            <button
              type="button"
              className="exhibition-schedule-more btn btn-link btn-sm"
              onClick={() => {
                setFullOpen(false);
                setQuery('');
                setCityFilter('');
              }}
            >
              Show less
            </button>
          )}
        </>
      )}
    </div>
  );
}

function RecentSchedule({ items }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, RECENT_INITIAL);
  const groups = useMemo(() => groupExhibitionsByMonth(visible), [visible]);
  const hidden = items.length - RECENT_INITIAL;

  return (
    <details className="exhibitions-panel exhibitions-panel--recent">
      <summary className="exhibitions-panel__summary">
        Past exhibitions
        <span className="exhibitions-panel__count">{items.length}</span>
      </summary>
      <div className="exhibitions-panel__body">
        <GroupedScheduleList groups={groups} muted scrollable={expanded && items.length > 8} />
        {hidden > 0 && !expanded && (
          <button type="button" className="exhibition-schedule-more btn btn-link btn-sm" onClick={() => setExpanded(true)}>
            Show {hidden} more past {hidden === 1 ? 'show' : 'shows'}
          </button>
        )}
        {expanded && hidden > 0 && (
          <button type="button" className="exhibition-schedule-more btn btn-link btn-sm" onClick={() => setExpanded(false)}>
            Show fewer
          </button>
        )}
      </div>
    </details>
  );
}

export default function HomeExhibitions({ upcoming = [], recent = [], status = 'loading' }) {
  const { settings: site } = useSiteSettings();
  const home = site.page_copy?.home || {};

  if (status !== 'ready') return null;
  if (upcoming.length + recent.length === 0) return null;

  return (
    <section id="exhibitions-shows" className="exhibitions-section" aria-labelledby="exhibitions-heading">
      <div className="exhibitions-section__container">
        <header className="exhibitions-section__header text-center">
          <p className="exhibitions-eyebrow">{home.exhibitions_eyebrow}</p>
          <h2 id="exhibitions-heading" className="section-title exhibitions-section__title">
            {home.exhibitions_title}
          </h2>
          <p className="section-subtitle exhibitions-section__subtitle">{home.exhibitions_subtitle}</p>
        </header>

        {upcoming.length > 0 && <UpcomingSchedule items={upcoming} />}
        {recent.length > 0 && <RecentSchedule items={recent} />}
      </div>
    </section>
  );
}
